import api from './api';

// Compliance Checker — hrms routers/compliance.py, the same backend and Access
// key (compliance-alerts) as hrpayroll's Settings → Organization page. The
// checks are derived on every request; only Ignore and the on/off switches are
// stored.

export const COMPLIANCE_PATH = '/employee/compliance';

export type ComplianceStatus = 'issue' | 'ignored' | 'ok';
export type ComplianceFix = 'company-compliance' | 'employee-salary' | 'tds-filing';

export interface ComplianceEmployee {
  employee_id: string;
  name: string;
  detail: string;
}

export interface ComplianceFinding {
  key: string;
  severity: 'action' | 'info';
  title: string;
  detail: string;
  fix: ComplianceFix;
  fingerprint: string;
  employees: ComplianceEmployee[];
}

export interface ComplianceCheck {
  key: string;
  area: string; // "PF" | "ESI" | "Professional Tax" | "TDS"
  title: string;
  about: string;
  status: ComplianceStatus;
  finding: ComplianceFinding | null;
  ignored_at: string | null;
}

export interface ComplianceSettings {
  company_enabled: boolean;
  user_enabled: boolean;
  show_alerts: boolean;
  can_manage: boolean;
}

export interface ComplianceChecksResponse extends ComplianceSettings {
  checks: ComplianceCheck[];
  checked_at: string;
}

export const complianceApi = {
  getChecks: () => api.get<ComplianceChecksResponse>('/compliance/checks'),
  ignore: (key: string, fingerprint: string) =>
    api.post(`/compliance/checks/${encodeURIComponent(key)}/ignore`, { fingerprint }),
  restore: (key: string) => api.delete(`/compliance/checks/${encodeURIComponent(key)}/ignore`),
  updateSettings: (settings: { company_enabled?: boolean; user_enabled?: boolean }) =>
    api.put<ComplianceSettings>('/compliance/settings', settings),
};

// The bell's feed: one row per person per open finding, task_id "compliance:<key>".
export const getComplianceNotifications = (limit = 50) =>
  api.get(`/compliance/notifications/unread?limit=${limit}`);
export const markComplianceNotificationRead = (id: string) =>
  api.post(`/compliance/notifications/${encodeURIComponent(id)}/read`);

// The web app, for the fixes Messenger has no screen for. A hosted Messenger
// sits beside it (qa.messenger.teamlio.ai → qa.teamlio.ai); the desktop build
// has no such host, so it needs REACT_APP_TEAMLIO_WEB_URL to leave production.
const teamlioWebUrl = () => {
  if (process.env.REACT_APP_TEAMLIO_WEB_URL) return process.env.REACT_APP_TEAMLIO_WEB_URL.replace(/\/+$/, '');
  const { protocol, hostname } = window.location;
  if (/(^|\.)messenger\.teamlio\.ai$/.test(hostname)) return `${protocol}//${hostname.replace(/(^|\.)messenger\./, '$1')}`;
  return 'https://teamlio.ai';
};

// Where "Correct" goes. Messenger has the Payroll Reports screen but neither
// the company's statutory switches nor the employee salary form, so those two
// open the web app.
export const complianceFixTarget = (fix: ComplianceFix, employeeId?: string): { to: string; external: boolean } => {
  if (fix === 'tds-filing') return { to: '/employee/payroll/reports?tab=statutory', external: false };
  const path = fix === 'employee-salary' && employeeId
    ? `/employees?edit=${encodeURIComponent(employeeId)}`
    : '/settings/company?tab=compliance';
  return { to: `${teamlioWebUrl()}${path}`, external: true };
};
